import axios from "axios";
import React, { createContext, useState, useEffect } from "react";
import { API_ENDPOINT } from "../utils/constants";
import { deleteToken, getHeader, getToken } from "../utils/util";

export const UserContext = createContext();

const isExpiredTokenError = (error) => {
  const status = error.response?.status;
  const responseData = error.response?.data;
  const message =
    typeof responseData === "string"
      ? responseData
      : responseData?.error || responseData?.message || "";

  return (
    status === 401 ||
    /(?:token|jwt).*expired|expired.*(?:token|jwt)/i.test(String(message))
  );
};

const clearSessionAndRedirectToLogin = () => {
  deleteToken();
  window.location.href = "/login";
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false); // Added loggingOut state

  const loadUser = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_ENDPOINT}/user/get-profile`,
        getHeader()
      );
      setUser(data);
    } catch (error) {
      if (getToken() && isExpiredTokenError(error)) {
        setUser(null);
        clearSessionAndRedirectToLogin();
        return;
      }

      console.error("Failed to load user", error);
      setUser(null);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const logoutUser = async () => {
    setLoggingOut(true);
    try {
      await axios.post(`${API_ENDPOINT}/user/logout`, {}, getHeader());
    } catch (error) {
      console.error("Failed to log out from server", error);
    } finally {
      setLoggingOut(false);
      clearSessionAndRedirectToLogin();
    }
  };

  const refreshUser = () => loadUser(false);

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        loggingOut, // Exposed to consumers
        reloadUser: () => loadUser(true),
        refreshUser,
        logoutUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
